import api from "../api/Client";
import { SecureStorage } from "../storage/SecureStorage";

export const APRENDE_PRODUCT_ID = 61;

type TaskMapEntry = {
  taskId?: number | null;
  coachId: number;
  dueDate: string;
  description: string;
};

const TASK_MAP_KEY = "aprende_task_map_v1";

function safeGetLocalStorage(key: string): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorage(key: string, value: string) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function loadTaskMap(): Record<string, TaskMapEntry> {
  const raw = safeGetLocalStorage(TASK_MAP_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveTaskMap(map: Record<string, TaskMapEntry>) {
  safeSetLocalStorage(TASK_MAP_KEY, JSON.stringify(map));
}

function normalizeUserName(name?: string | null) {
  const trimmed = String(name || "").trim();
  return trimmed.length > 0 ? trimmed : "usuario";
}

async function resolveUserName(fallback?: string | null) {
  if (fallback && String(fallback).trim()) return normalizeUserName(fallback);
  try {
    const user = await SecureStorage.getUser();
    const fromUser =
      user?.fullName ??
      user?.name ??
      user?.nombre ??
      user?.email ??
      null;
    return normalizeUserName(fromUser);
  } catch {
    const raw = safeGetLocalStorage("user");
    try {
      const parsed = raw ? JSON.parse(raw) : {};
      const fromUser =
        parsed?.fullName ??
        parsed?.name ??
        parsed?.nombre ??
        parsed?.email ??
        null;
      return normalizeUserName(fromUser);
    } catch {
      return "usuario";
    }
  }
}

export function extractBookingId(data: any): number | null {
  const id = Number(
    data?.booking_id ??
      data?.bookingId ??
      data?.id ??
      data?.booking?.id ??
      null
  );
  return Number.isFinite(id) ? id : null;
}

export function resolveBaseProductId(
  selectedProductId: number | string | null | undefined,
  userProducts: Array<{ id: number; active_product_id?: number | null }>
): number | null {
  const num = Number(selectedProductId);
  if (!Number.isFinite(num)) return null;
  const match = userProducts.find(
    (p) =>
      Number(p.active_product_id ?? p.id) === num || Number(p.id) === num
  );
  return match ? Number(match.id) : num;
}

function addDaysToYmd(startYmd: string, days: number): string | null {
  if (!startYmd || startYmd.length < 10) return null;
  const [y, m, d] = startYmd.slice(0, 10).split("-").map(Number);
  if (![y, m, d].every(Number.isFinite)) return null;
  const date = new Date(y, (m || 1) - 1, d || 1, 12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function buildDescription(userName: string) {
  return `Rellena el formulario de Aprende a entrenar del usuario (${userName})`;
}

export async function createAprendeTask(params: {
  bookingId?: number | null;
  coachId?: number | null;
  startDateYmd?: string | null;
  userName?: string | null;
}) {
  const coachId = Number(params.coachId);
  if (!Number.isFinite(coachId)) return;
  const startDate = params.startDateYmd?.slice(0, 10) || "";
  const dueDate = addDaysToYmd(startDate, 7);
  if (!dueDate) return;

  const bookingId = params.bookingId ?? null;
  const map = loadTaskMap();
  if (bookingId && map[String(bookingId)]) return;

  const userName = await resolveUserName(params.userName);
  const description = buildDescription(userName);

  const payload = {
    coach_id: coachId,
    description,
    type: "one_shot",
    frecuency: "None",
    limit_date: dueDate,
    done: 0,
    coach_comment: "",
  };

  try {
    const res = await api.post("/api/coaches/tasks", payload);
    const taskId = Number(
      (res as any)?.data?.id ??
        (res as any)?.data?.task_id ??
        (res as any)?.data?.taskId ??
        null
    );
    if (bookingId) {
      map[String(bookingId)] = {
        taskId: Number.isFinite(taskId) ? taskId : null,
        coachId,
        dueDate,
        description,
      };
      saveTaskMap(map);
    }
  } catch (err) {
    console.warn("No se pudo crear tarea de Aprende a entrenar:", err);
  }
}

export async function deleteAprendeTask(params: {
  bookingId?: number | null;
  coachId?: number | null;
  startDateYmd?: string | null;
  userName?: string | null;
}) {
  const bookingId = params.bookingId ?? null;
  if (!bookingId) return;
  const map = loadTaskMap();
  const entry = map[String(bookingId)];
  const coachId = Number(entry?.coachId ?? params.coachId);
  if (!Number.isFinite(coachId)) return;

  const startDate = params.startDateYmd?.slice(0, 10) || "";
  const dueDate = entry?.dueDate ?? addDaysToYmd(startDate, 7);
  const userName = await resolveUserName(params.userName);
  const description = entry?.description ?? buildDescription(userName);

  let taskId = entry?.taskId ?? null;
  if (!Number.isFinite(Number(taskId))) {
    try {
      const res = await api.get("/api/coaches/tasks", { params: { coach_id: coachId } });
      const tasks: any[] = Array.isArray((res as any)?.data) ? (res as any).data : [];
      const match = tasks.find((t) => {
        const tDesc = String(t?.description || "");
        const tLimit = String(t?.limit_date || "").slice(0, 10);
        if (!tDesc || !dueDate) return false;
        if (tDesc !== description) return false;
        if (tLimit && tLimit !== dueDate) return false;
        return true;
      });
      const foundId = Number(match?.id ?? null);
      if (Number.isFinite(foundId)) taskId = foundId;
    } catch (err) {
      console.warn("No se pudo buscar la tarea de Aprende a entrenar:", err);
    }
  }

  if (Number.isFinite(Number(taskId))) {
    try {
      await api.delete(`/api/coaches/tasks/${taskId}`);
    } catch (err) {
      try {
        await api.patch(`/api/coaches/tasks/${taskId}/complete`);
      } catch (inner) {
        console.warn("No se pudo eliminar/completar la tarea:", inner);
      }
    }
  }

  if (map[String(bookingId)]) {
    delete map[String(bookingId)];
    saveTaskMap(map);
  }
}
